import React, { useEffect, useState } from 'react';
import { useUserData } from '@/hooks/useUserData';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, LockKeyhole, Rocket } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

const IdeaValidationGate: React.FC<Props> = ({ children }) => {
  const { profile, loading } = useUserData();
  const { user } = useAuth();
  const [derivedValidated, setDerivedValidated] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      if (loading) return;
      if (profile?.idea_validated) return;
      if (!user?.id) return;
      setChecking(true);
      try {
        const { data, error } = await supabase
          .from('idea_assessments')
          .select('id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (data?.id) setDerivedValidated(true);
      } catch (e) {
        // silent fallback
      } finally {
        setChecking(false);
      }
    };
    run();
  }, [loading, profile?.idea_validated, user?.id]);

  const ideaValidated = Boolean(profile?.idea_validated) || derivedValidated;

  if (loading || checking) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <div className="animate-pulse text-gray-500">Loading your workspace…</div>
      </div>
    );
  }

  if (!ideaValidated) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="max-w-xl w-full rounded-2xl bg-white/70 backdrop-blur-md shadow-neumorphic p-8 text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">
            <LockKeyhole className="h-6 w-6" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">Unlock this module</h2>
          <p className="mt-2 text-slate-600">
            Complete your idea validation to access this premium feature. Our AI will review your idea and help you focus on the highest ROI path.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/app/idea-validation')}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 text-white px-4 py-2.5 hover:bg-teal-700 transition shadow-md"
            >
              <ShieldCheck className="h-5 w-5" />
              Start Validation
            </button>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 text-slate-800 px-4 py-2.5 hover:bg-slate-200 transition"
            >
              <Rocket className="h-5 w-5" />
              Upgrade for Pro Tools
            </Link>
          </div>
          <p className="mt-3 text-xs text-slate-500">Already validated? Refresh the page after completion.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default IdeaValidationGate;
